import { Card, Typography, Stack, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useWarehouseKitRequest } from 'contexts/WarehouseKitRequestContext';
import { useGetShipmentsClientV2Query } from 'store/slices/clientV2/shipments';
import {
  stockLocationPurposeLabels,
  StockLocationSchemaLite,
  binPurposeIconMap,
} from 'types/clientV2/stockLocations';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { ShipmentSchema, ShipmentSchemaLite } from 'types/clientV2/shipments';
import { PropertySubsetValidator } from 'types/api';
import { useOrgAndTeamContext } from 'views/warehouse/OrgAndTeamContext';

enum CardState {
  SELECTED = 'selected',
  HIGHLIGHTED = 'highlighted',
}

interface BinLocationCardProps {
  stockLocation: PropertySubsetValidator<
    StockLocationSchemaLite,
    'purpose' | 'kitRequest' | 'shipment' | 'org' | 'name' | 'team'
  >;
  selected?: boolean;
  highlighted?: boolean;
  onClick?: () => void;
}

export const BinLocationCard = ({
  stockLocation,
  selected = false,
  highlighted = false,
  onClick,
}: BinLocationCardProps) => {
  const { getOrgOrTeamDisplay } = useOrgAndTeamContext();

  const { kitRequests } = useWarehouseKitRequest();

  const theme = useTheme();
  const PurposeIcon = stockLocation.purpose
    ? binPurposeIconMap[stockLocation.purpose]
    : null;

  const cardState: CardState | undefined = highlighted
    ? CardState.HIGHLIGHTED
    : selected
    ? CardState.SELECTED
    : undefined;

  const getColors = (state: CardState | undefined) => {
    switch (state) {
      case CardState.HIGHLIGHTED:
        return {
          border: theme.palette.success.main,
          text: theme.palette.common.black,
          divider: theme.palette.success.main,
        };
      case CardState.SELECTED:
        return {
          border: theme.palette.success.main,
          background: theme.palette.success.main,
          text: theme.palette.common.white,
          divider: theme.palette.common.white,
        };
      default:
        return {
          border: theme.palette.grey[500],
          text: theme.palette.common.black,
          divider: theme.palette.grey[500],
        };
    }
  };

  const colors = getColors(cardState);

  const kitRequest = kitRequests.find(
    (kr) => kr.id === stockLocation.kitRequest
  );

  const { data: shipmentData } = useServerSideQuery<
    ShipmentSchema,
    Pick<
      ShipmentSchemaLite,
      | 'id'
      | 'shipmentCode'
      | 'trackingNumber'
      | 'org'
      | 'shipTo'
      | 'shippedAt'
      | 'approvedAt'
    >
  >(
    useGetShipmentsClientV2Query,
    stockLocation.shipment
      ? {
          schema: [
            'id',
            'shipmentCode',
            'trackingNumber',
            'org',
            'shipTo',
            'shippedAt',
            'approvedAt',
          ],
        }
      : skipToken
  );

  const shipment = shipmentData?.data?.[0] || null;

  const orgOrTeamDisplay = getOrgOrTeamDisplay(
    stockLocation.org,
    stockLocation.team
  );

  const hasRelatedRecord = Boolean(
    kitRequest?.kitCode || shipment?.shipmentCode || orgOrTeamDisplay
  );

  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        width: '300px',
        height: '105px',
        borderColor: colors.border,
        bgcolor: colors.background,
        borderWidth: cardState === CardState.HIGHLIGHTED ? 2 : 1,
        cursor: 'pointer',
      }}
    >
      <Stack direction="row" alignItems="stretch" spacing={0}>
        {stockLocation.purpose && (
          <>
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ minWidth: 90, p: 2 }}
              spacing={1}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 500,
                  color: colors.text,
                }}
              >
                {stockLocationPurposeLabels[stockLocation.purpose]}
              </Typography>
              {PurposeIcon && <PurposeIcon size={40} color={colors.text} />}
            </Stack>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: colors.divider }}
            />
          </>
        )}

        <Stack flex={1} justifyContent="center" width="100%">
          <Stack
            alignItems="flex-end"
            width="100%"
            direction="column"
            justifyContent="center"
            spacing={1}
            sx={{
              height: '100%',
            }}
          >
            {stockLocation.purpose && hasRelatedRecord && (
              <>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      wordBreak: 'break-all',
                      color: colors.text,
                      p: 1,
                      lineHeight: 1,
                    }}
                  >
                    {kitRequest?.kitCode ||
                      shipment?.shipmentCode ||
                      orgOrTeamDisplay}
                  </Typography>
                </Stack>

                <Divider flexItem sx={{ borderColor: colors.divider }} />
              </>
            )}
            <Typography
              variant="h3"
              sx={{
                fontWeight: 600,
                p: 1,
                color: colors.text,
                lineHeight: 1,
              }}
            >
              {stockLocation.name}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
